// Verified deep links into sister properties of The Shrink Network.
//
// Every slug and path pattern in this file was checked against the live
// sitemap of the target property (July 2026). When a sister site adds or
// renames pages, update this file; nothing here is guessed.
//
// All URLs are built from the site.ts constants. Callers wrap the result in
// networkUrl() with a contextual campaign before rendering.

import {
  SHRINKTIONARY_URL,
  ANXIETYRESEARCH_URL,
  DEPRESSION_RESOURCE_URL,
  SHRINKDAILY_URL,
} from "./site";

// ---------------------------------------------------------------------------
// Shrinktionary: the language layer.
// ---------------------------------------------------------------------------

/**
 * AR glossary slugs that exist verbatim as Shrinktionary /terms/ entries.
 * Verified against the Shrinktionary sitemap (252 term pages).
 */
export const SHRINKTIONARY_TERM_SLUGS: ReadonlySet<string> = new Set([
  "avoidance",
  "brain-fog",
  "burnout",
  "catastrophizing",
  "cbt",
  "cortisol",
  "depersonalization",
  "derealization",
  "emotional-regulation",
  "exposure-therapy",
  "hyperventilation",
  "hypervigilance",
  "insomnia",
  "panic-attack",
  "perfectionism",
  "racing-thoughts",
  "rumination",
  "snri",
  "ssri",
]);

export function shrinktionaryTerm(slug: string): string {
  return `${SHRINKTIONARY_URL}/terms/${slug}/`;
}

/**
 * Shrinktionary language-cluster fallback for AR glossary categories, used
 * when the exact term has no Shrinktionary entry. Cluster slugs verified
 * against the live /clusters/ index.
 */
const SHRINKTIONARY_CLUSTER_BY_GLOSSARY_CATEGORY: Record<string, string> = {
  symptoms: "anxiety-language",
  "panic-nervous-system": "anxiety-language",
  "thinking-patterns": "overthinking-language",
  "psychiatry-treatment": "therapy-language",
  "stress-burnout": "anxiety-language",
};

// Medication-flavored glossary slugs route to the medication cluster instead
// of the therapy cluster.
const MEDICATION_LANGUAGE_SLUGS: ReadonlySet<string> = new Set([
  "medication-management",
  "side-effects",
]);

/**
 * Best Shrinktionary destination for a glossary entry: the exact term page
 * when it exists, otherwise the matching language cluster, otherwise the
 * A-to-Z glossary index.
 */
export function shrinktionaryFor(
  slug: string,
  category: string,
): { url: string; exact: boolean } {
  if (SHRINKTIONARY_TERM_SLUGS.has(slug)) {
    return { url: shrinktionaryTerm(slug), exact: true };
  }
  if (MEDICATION_LANGUAGE_SLUGS.has(slug)) {
    return {
      url: `${SHRINKTIONARY_URL}/clusters/medication-language/`,
      exact: false,
    };
  }
  const cluster = SHRINKTIONARY_CLUSTER_BY_GLOSSARY_CATEGORY[category];
  if (cluster) {
    return { url: `${SHRINKTIONARY_URL}/clusters/${cluster}/`, exact: false };
  }
  return { url: `${SHRINKTIONARY_URL}/terms/`, exact: false };
}

// ---------------------------------------------------------------------------
// AnxietyResearch: the evidence layer.
// ---------------------------------------------------------------------------

/**
 * Per-state anxiety statistics page. AnxietyResearch publishes one for every
 * state slug AR uses (all 50 states plus DC, verified 1:1).
 */
export function anxietyResearchStateStats(stateSlug: string): string {
  return `${ANXIETYRESEARCH_URL}/statistics/states/${stateSlug}/`;
}

/**
 * Treatment article slug to the AnxietyResearch evidence summary that covers
 * the same intervention. Summary slugs verified against the live
 * /evidence-summaries/ index.
 */
const EVIDENCE_SUMMARY_BY_ARTICLE: Record<string, string> = {
  "zoloft-for-anxiety": "ssris-snris-for-anxiety",
  "lexapro-for-anxiety": "ssris-snris-for-anxiety",
  "prozac-for-anxiety": "ssris-snris-for-anxiety",
  "cymbalta-for-anxiety": "ssris-snris-for-anxiety",
  "effexor-for-anxiety": "ssris-snris-for-anxiety",
  "snris-explained": "ssris-snris-for-anxiety",
  "how-cbt-and-ssris-work": "ssris-snris-for-anxiety",
  "lexapro-vs-zoloft-for-anxiety": "ssris-snris-for-anxiety",
  "lexapro-vs-prozac-for-anxiety": "ssris-snris-for-anxiety",
  "effexor-vs-cymbalta-for-anxiety": "ssris-snris-for-anxiety",
  "buspirone-vs-ssri-for-anxiety": "ssris-snris-for-anxiety",
  benzodiazepines: "benzodiazepines",
  "ativan-for-anxiety": "benzodiazepines",
  "klonopin-for-anxiety": "benzodiazepines",
  "xanax-for-anxiety": "benzodiazepines",
  "benzodiazepine-vs-ssri-for-panic": "benzodiazepines",
  "exposure-therapy-explained": "exposure-therapy",
  "mindfulness-based-therapies": "mindfulness-based-interventions",
  "act-for-anxiety": "mindfulness-based-interventions",
  "finding-psychiatric-care": "telepsychiatry-for-anxiety",
  "what-a-psychiatry-visit-involves": "telepsychiatry-for-anxiety",
};

const EVIDENCE_SUMMARY_LABELS: Record<string, string> = {
  "ssris-snris-for-anxiety": "SSRIs and SNRIs for anxiety",
  benzodiazepines: "benzodiazepines",
  "exposure-therapy": "exposure therapy",
  "mindfulness-based-interventions": "mindfulness-based interventions",
  "telepsychiatry-for-anxiety": "telepsychiatry for anxiety",
};

/**
 * Evidence summary link for a treatment article, or undefined when the
 * article has no matching summary.
 */
export function evidenceSummaryFor(
  articleSlug: string,
): { url: string; label: string } | undefined {
  const summary = EVIDENCE_SUMMARY_BY_ARTICLE[articleSlug];
  if (!summary) return undefined;
  return {
    url: `${ANXIETYRESEARCH_URL}/evidence-summaries/${summary}/`,
    label: EVIDENCE_SUMMARY_LABELS[summary],
  };
}

// ---------------------------------------------------------------------------
// DepressionResource: the sister practical layer.
// ---------------------------------------------------------------------------

/**
 * Per-state depression resource page. DepressionResource publishes one for
 * every state slug AR uses (verified 1:1).
 */
export function depressionResourceState(stateSlug: string): string {
  return `${DEPRESSION_RESOURCE_URL}state-resources/${stateSlug}/`;
}

// ---------------------------------------------------------------------------
// ShrinkDaily: the daily learning layer.
// ---------------------------------------------------------------------------

/**
 * ShrinkDaily "science of" pathway for each AR article category. Pathway
 * slugs verified against the live /pathway/ pages.
 */
const SHRINKDAILY_PATHWAY_BY_CATEGORY: Record<
  string,
  { slug: string; label: string }
> = {
  symptoms: {
    slug: "the-science-of-emotion-regulation",
    label: "The science of emotion regulation",
  },
  "panic-nervous-system": {
    slug: "the-science-of-emotion-regulation",
    label: "The science of emotion regulation",
  },
  "overthinking-rumination": {
    slug: "the-science-of-overthinking",
    label: "The science of overthinking",
  },
  "work-performance": {
    slug: "the-science-of-burnout",
    label: "The science of burnout",
  },
  "relationships-social": {
    slug: "the-science-of-connection",
    label: "The science of connection",
  },
  "treatment-education": {
    slug: "the-science-of-habits",
    label: "The science of habits",
  },
};

export function shrinkDailyPathwayFor(
  categorySlug: string,
): { url: string; label: string } | undefined {
  const p = SHRINKDAILY_PATHWAY_BY_CATEGORY[categorySlug];
  if (!p) return undefined;
  // No trailing slash: ShrinkDaily's sitemap canonicals omit it, unlike the
  // other properties. Matching each site's own canonical avoids a redirect.
  return { url: `${SHRINKDAILY_URL}/pathway/${p.slug}`, label: p.label };
}

/**
 * ShrinkDaily single-concept deep links for each Anxiety Map. Each map points
 * to the two concepts that name the mechanisms inside its cluster. Concept
 * slugs were verified live (July 2026) against shrinkdaily.com/concept/<slug>;
 * only pages that returned 200 are included (scheduled-worry returned 404 and
 * is deliberately omitted).
 */
const SHRINKDAILY_CONCEPTS_BY_MAP: Record<
  string,
  { slug: string; label: string }[]
> = {
  panic: [
    { slug: "fight-or-flight-response", label: "Fight or flight response" },
    { slug: "interoceptive-exposure", label: "Interoceptive exposure" },
  ],
  overthinking: [
    { slug: "rumination", label: "Rumination" },
    { slug: "catastrophizing", label: "Catastrophizing" },
  ],
  "social-anxiety": [
    { slug: "anticipatory-anxiety", label: "Anticipatory anxiety" },
    { slug: "avoidance", label: "Avoidance" },
  ],
  "sleep-and-anxiety": [
    { slug: "worry", label: "Worry" },
    { slug: "hypervigilance", label: "Hypervigilance" },
  ],
  "high-performer": [
    {
      slug: "intolerance-of-uncertainty",
      label: "Intolerance of uncertainty",
    },
    { slug: "catastrophizing", label: "Catastrophizing" },
  ],
  "health-anxiety": [
    { slug: "hypervigilance", label: "Hypervigilance" },
    { slug: "interoceptive-exposure", label: "Interoceptive exposure" },
  ],
  "generalized-anxiety": [
    { slug: "worry", label: "Worry" },
    {
      slug: "intolerance-of-uncertainty",
      label: "Intolerance of uncertainty",
    },
  ],
  ocd: [
    {
      slug: "intolerance-of-uncertainty",
      label: "Intolerance of uncertainty",
    },
    { slug: "avoidance", label: "Avoidance" },
  ],
  trauma: [
    { slug: "fight-or-flight-response", label: "Fight or flight response" },
    { slug: "hypervigilance", label: "Hypervigilance" },
  ],
};

/**
 * ShrinkDaily concept links for an Anxiety Map slug, or an empty array when the
 * map has no mapping. Callers wrap each url in networkUrl() before rendering.
 */
export function shrinkDailyConceptsFor(
  mapSlug: string,
): { url: string; label: string }[] {
  const concepts = SHRINKDAILY_CONCEPTS_BY_MAP[mapSlug];
  if (!concepts) return [];
  // No trailing slash, matching the pathway rule above (ShrinkDaily canonicals
  // omit it), which avoids a redirect.
  return concepts.map((c) => ({
    url: `${SHRINKDAILY_URL}/concept/${c.slug}`,
    label: c.label,
  }));
}
